import type { IObservableArray } from 'mobx';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { getOmniAwarenessTargeting } from '@feathr/extender/components/Targetings';
import { useCampaignChannelVisibility } from '@feathr/extender/hooks';
import type { ListResponse } from '@feathr/rachis';
import type { IUseAudienceBreakdown } from '@feathr/report_components';

import { useEstimatedReach } from './AdWizardAudienceStep.EstimatedReach.hooks';

// The awareness slice persists its include_percent in its `name` (see useOmniSliceTargeting);
// defaults to 100 when no slice exists yet.
function getOmniSliceIncludePercent(targetings: IObservableArray<Targeting>): number {
  const sliceName = getOmniAwarenessTargeting(targetings)?.get('name');
  const parsed = Number(sliceName);
  return sliceName !== undefined && !Number.isNaN(parsed) ? parsed : 100;
}

interface IUseReachValidationProps {
  /** The campaign whose reachable audience should be validated. */
  campaign: Campaign;
  /** The list of targetings associated with the campaign. */
  targetings: ListResponse<Targeting>;
  /** The shared audience breakdown computed once at the wizard level. */
  audienceData: IUseAudienceBreakdown;
}

/**
 * The per-channel reachable audience for a campaign, plus its loading state. Display and Meta
 * are gated independently, so each has its own count and an "applies" flag for whether the
 * campaign serves on that channel.
 */
export interface IReachValidationResult {
  /** Display/Trade Desk reachable people. */
  displayReach: number;
  /** Meta reachable people. */
  metaReach: number;
  /** Whether the campaign serves on the display/Trade Desk channel. */
  appliesToDisplay: boolean;
  /** Whether the campaign serves on the Meta channel. */
  appliesToMeta: boolean;
  /** Whether the reach counts are still being calculated. */
  isLoading: boolean;
}

/**
 * Computes the per-channel reachable audience for the minimum-audience gate. Display and Meta
 * each have their own 2,000 floor, so this returns both counts and which channels the campaign
 * serves on. Omni Reach uses the channel forecast; everything else uses the shared breakdown
 * (whose total folds in Meta only when showMeta is true, so Meta is subtracted back out for the
 * display count). Segment retargeting always serves on display and never on Meta.
 *
 * @param props - Hook configuration.
 * @param props.campaign - The campaign to compute reachable audience for.
 * @param props.targetings - The list of targetings for the campaign.
 * @param props.audienceData - The shared audience breakdown computed once at the wizard level.
 * @returns The per-channel reach counts, which channels apply, and the loading state.
 */
export function useReachValidation({
  campaign,
  targetings,
  audienceData,
}: IUseReachValidationProps): IReachValidationResult {
  const { showDisplay, showMeta } = useCampaignChannelVisibility(campaign);
  const { isOmniCampaign, isOmniReachCampaign } = campaign;

  const estimatedReach = useEstimatedReach({ targetings, campaign });

  // Segment retargeting always serves on display and never on Meta; Omni follows its channels.
  const appliesToDisplay = isOmniCampaign ? showDisplay : true;
  const appliesToMeta = isOmniCampaign ? showMeta : false;

  if (isOmniReachCampaign) {
    // #8674: gate on the slider-narrowed reach (what the user sees), not the full forecast.
    const includeFraction = getOmniSliceIncludePercent(targetings.models) / 100;
    return {
      displayReach: (estimatedReach.displayReach ?? 0) * includeFraction,
      metaReach: estimatedReach.metaReach ?? 0,
      appliesToDisplay,
      appliesToMeta,
      isLoading: estimatedReach.isLoading,
    };
  }

  return {
    // The breakdown total folds in Meta only when showMeta is true, so subtract it back out.
    displayReach:
      audienceData.numTargetedReachableUsers - (showMeta ? audienceData.numTargetedMeta : 0),
    metaReach: audienceData.numTargetedMeta,
    appliesToDisplay,
    appliesToMeta,
    isLoading: audienceData.isLoading,
  };
}
